import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
  }, []);

  const isHeroTransparent = isHomePage && !scrolled;

  const logoColor = isHeroTransparent ? 'text-background-50' : 'text-foreground-950';
  const linkColor = isHeroTransparent ? 'text-background-50/90 hover:text-background-50' : 'text-foreground-700 hover:text-primary-500';
  const catBtnStyle = isHeroTransparent
    ? 'bg-background-50/20 text-background-50 hover:bg-background-50/30 backdrop-blur-sm'
    : 'bg-foreground-900 text-background-50 hover:bg-foreground-800';
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 whitespace-nowrap cursor-pointer ${isHeroTransparent ? 'text-background-50' : 'text-foreground-950'}`}
          >
            <span className="text-lg md:text-xl font-heading font-bold tracking-tight">
              ACBuy<span className={isHeroTransparent ? 'text-accent-400' : 'text-primary-500'}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkColor}`}
            >
              Home
            </Link>
            <Link
              to="/acbuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkColor}`}
            >
              Shopping Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${catBtnStyle}`}
              >
                Categories
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-sm"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-sm"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              ACBuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${isHeroTransparent ? 'text-background-50 hover:bg-background-50/20' : 'text-foreground-800 hover:bg-background-100'}`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-3 px-2 bg-background-50 rounded-b-xl">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-primary-50 rounded-lg cursor-pointer transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-primary-500 text-sm"></i>
                </div>
                Home
              </Link>
              <Link
                to="/acbuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-primary-50 rounded-lg cursor-pointer transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-accent-500 text-sm"></i>
                </div>
                Shopping Guide
              </Link>

              <div className="border-t border-background-100 mt-1 pt-3">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-primary-50 rounded-lg cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-grid-line text-secondary-600 text-sm"></i>
                    </div>
                    Categories
                  </div>
                  {mobileCatOpen ? (
                    <i className="ri-arrow-up-s-line text-foreground-500"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-foreground-500"></i>
                  )}
                </button>
                {mobileCatOpen && (
                  <div className="mt-1 ml-10 grid grid-cols-2 gap-0.5">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => {
                          handleCategoryClick(cat.url);
                          setMobileMenuOpen(false);
                        }}
                        className="text-left px-2 py-2 text-xs text-foreground-600 hover:text-primary-500 hover:bg-primary-50 rounded-md cursor-pointer transition-colors"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-3 border-t border-background-100 mt-1">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full text-center px-4 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  ACBuy Spreadsheet
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full text-center px-4 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}